// surfaceShader.ts — Per-FRAGMENT biome classification for the planet surface.
// The geometry carries a normalized elevation + latitude per vertex (terrain.ts);
// classifying those *after* interpolation (instead of baking a vertex colour and
// letting Gouraud smear it) gives crisp coastlines, sharp snow lines, and clean
// biome bands no matter how coarse the mesh. We keep MeshStandardMaterial — so we
// get its PBR lighting + tone mapping for free — and only patch it: override the
// albedo from the biome ramp, drop roughness over water for a real sun glint, and
// fold in the analytic eclipse shadow (shadow.ts). One onBeforeCompile does all
// three (a material gets only one).
import { Color, Vector2, Vector3, Vector4 } from 'three';
import type { IUniform } from 'three';
import type { WebGLProgramParametersWithUniforms } from 'three';
import type { PlanetKind } from '../../types';
import { PLANET_PRESETS } from './presets';
import { SHADOW_PARS } from '../shadow';
import type { ShadowUniforms } from '../shadow';
import { FBM3D, SIMPLEX3D } from './glslNoise';

const MAX_BANDS = 6;

// Hard cap on coastal cities painted per planet (matches cities.ts's 50-city cap);
// the GLSL loop is bounded by this and skips the unused tail via uCityCount.
const MAX_CITIES = 50;

export interface BiomeUniforms {
  [name: string]: IUniform<unknown>;
}

export interface CityUniforms {
  [name: string]: IUniform<unknown>;
}

// City uniforms for the focused planet's coastal settlements. Filled by Planet.tsx
// from cities.ts. Each uCity entry packs the object-space unit direction in xyz and
// the footprint's angular radius in w (one vec4 array, kind to the uniform budget).
// uCityCount is 0 on uninhabited/unfocused worlds, short-circuiting the city loop.
export function makeCityUniforms(): CityUniforms {
  return {
    uCityCount: { value: 0 },
    uCity: { value: Array.from({ length: MAX_CITIES }, () => new Vector4(1, 0, 0, 0)) },
    // Global urbanization 0..1: a planet-wide floor on urban density that fills the
    // land between metros, so a high-population world's cities merge into a single
    // planet-spanning ecumenopolis (Coruscant/Trantor). Driven by population.
    uCityUrban: { value: 0 },
    // Warm sodium-vapour night glow + a desaturated concrete grey for the daytime
    // urban sprawl. Bright HDR magnitude on the light so it reads as glowing dots
    // through the ACES tone map.
    uCityLight: { value: new Color('#ffce85') },
    uCityDayColor: { value: new Color('#6a655c') },
    uCityGlow: { value: 3.0 },
  };
}

// Build the ramp uniforms for a planet kind. Colours go in as linear-working-space
// (three's Color converts the sRGB hex), matching the space diffuseColor lives in.
// Climate inputs the surface shader needs to place ice caps / vegetation / scorch by
// physics: the day/night/mean temperatures (degC, the same the sidebar shows), a
// derived obliquity (0..1; see Planet.tsx), and per-kind frost/scorch gates.
export interface SurfaceClimate {
  meanTempC: number;
  dayTempC: number;
  nightTempC: number;
  obliquity: number;
  canFrost: boolean;
  canScorch: boolean;
}

export function makeBiomeUniforms(
  kind: PlanetKind,
  tint: string,
  seed = 0,
  climate: SurfaceClimate = {
    meanTempC: 15,
    dayTempC: 20,
    nightTempC: 10,
    obliquity: 0.25,
    canFrost: true,
    canScorch: true,
  },
  tidalLock = 0,
): BiomeUniforms {
  const ramp = PLANET_PRESETS[kind].ramp;
  const tintPercent = PLANET_PRESETS[kind].tintPercent;
  const bandColors: Color[] = [];
  const bands = ramp.bands?.stops ?? [];
  for (let i = 0; i < MAX_BANDS; i++) {
    bandColors.push(new Color(bands[Math.min(i, bands.length - 1)] ?? '#000000'));
  }
  // Per-planet offset into the (deterministic) noise field so every gas giant's
  // turbulence and storms are distinct rather than carbon copies.
  const s = seed >>> 0;
  const gasSeed = new Vector3(
    ((s & 1023) / 1023) * 60 - 30,
    (((s >> 10) & 1023) / 1023) * 60 - 30,
    (((s >> 20) & 1023) / 1023) * 60 - 30,
  );
  return {
    uSeaLevel: { value: ramp.seaLevel01 },
    uOceanFalloff: { value: ramp.oceanDepthFalloff },
    uDeepOcean: { value: new Color(ramp.deepOcean) },
    uShallowOcean: { value: new Color(ramp.shallowOcean) },
    uBeach: { value: new Color(ramp.beach) },
    uGrassLow: { value: new Color(ramp.grassLow) },
    uGrassHigh: { value: new Color(ramp.grassHigh) },
    uRockLow: { value: new Color(ramp.rockLow) },
    uRockHigh: { value: new Color(ramp.rockHigh) },
    uSnow: { value: new Color(ramp.snow) },
    uBeachWidth: { value: ramp.beachWidth },
    uMaxFlat: { value: ramp.maxFlatHeight },
    uSnowEq: { value: ramp.snowHeightEquator },
    uSnowPole: { value: ramp.snowHeightPole },
    uTintColor: { value: new Color(tint) },
    uTintPercent: { value: tintPercent },
    uIsGas: { value: ramp.bands ? 1 : 0 },
    uBands: { value: bandColors },
    uBandCount: { value: bands.length },
    uBandSharp: { value: ramp.bands?.sharpness ?? 0.35 },
    uGasSeed: { value: gasSeed },
    // LOD ramp (0..1): the fine streaks + storm vortices only resolve up close, so
    // distant gas giants stay smooth bands and don't shimmer. Driven by Planet.tsx.
    uGasDetail: { value: 0 },
    // Seconds (render clock) — drives the gas giant's slow zonal flow. Driven by
    // Planet.tsx; left at 0 (static) for non-gas kinds, which ignore it.
    uTime: { value: 0 },
    // Gas-giant flow, kept BOUNDED so it never grains under fast-forward (the game
    // clock reaches millions of seconds at 1,000,000×, where an unbounded uTime fed
    // into rotation/fBm-coords would (a) shear the noise into sub-pixel striations and
    // (b) overflow float32 precision). All the time reduction is done in JS float64 in
    // Planet.tsx; the shader only ever sees small bounded values, so it freezes crisply
    // at extreme speed instead of shattering into speckle. (See gameClock / Planet.tsx.)
    //   uGasRot:   (cos, sin) of the RIGID whole-deck zonal rotation — wrapped mod 2π
    //              in JS, so it spins forever without precision loss and, being rigid,
    //              never shears the structure.
    //   uGasSway:  a single bounded [-1,1] oscillator driving a small per-latitude
    //              countercurrent sway (bounded differential shear — belts rock past
    //              one another instead of winding apart without bound).
    //   uGasChurn: four bounded oscillators replacing the old linear uTime offsets in
    //              the domain-warp / storm fBm coords, so those coords stay small.
    uGasRot: { value: new Vector2(1, 0) },
    uGasSway: { value: 0 },
    uGasChurn: { value: new Vector4(0, 0, 0, 0) },
    // Climate (degC) + derived obliquity (0..1) + per-kind frost/scorch gates. The
    // shader builds a per-fragment temperature from these (latitude for rotators,
    // substellar angle for tidally-locked worlds) and derives ice caps / vegetation
    // parch / glaciers from it. uSeaIce is the frozen-sea colour.
    uMeanT: { value: climate.meanTempC },
    uDayT: { value: climate.dayTempC },
    uNightT: { value: climate.nightTempC },
    uObliq: { value: climate.obliquity },
    uCanFrost: { value: climate.canFrost ? 1 : 0 },
    uCanScorch: { value: climate.canScorch ? 1 : 0 },
    // 1 for tidally-locked worlds: the shader freezes their permanent night face.
    uTidalLock: { value: tidalLock },
    uSeaIce: { value: new Color('#cfe0e8') },
    // Broad molten-lows self-illumination: 1 for a truly molten world (its lava seas
    // glow), tapered for a fractured crust, 0 for a volcanic world — which stays a
    // DARK basalt ball lit only along its spiderweb fissures (uMoltenLows gates the
    // broad lows glow in emissivemap_fragment; the veins glow regardless).
    uMoltenLows: { value: kind === 'molten' ? 1.0 : kind === 'broken' ? 0.55 : 0.0 },
  };
}

// GLSL: mirrors biome.ts but per fragment. smoothstep edges across the interpolated
// height yield crisp transitions independent of triangle size.
const BIOME_PARS = /* glsl */ `
  uniform float uSeaLevel;
  uniform float uOceanFalloff;
  uniform vec3 uDeepOcean;
  uniform vec3 uShallowOcean;
  uniform vec3 uBeach;
  uniform vec3 uGrassLow;
  uniform vec3 uGrassHigh;
  uniform vec3 uRockLow;
  uniform vec3 uRockHigh;
  uniform vec3 uSnow;
  uniform float uBeachWidth;
  uniform float uMaxFlat;
  uniform float uSnowEq;
  uniform float uSnowPole;
  uniform vec3 uTintColor;
  uniform float uTintPercent;
  uniform float uIsGas;
  uniform vec3 uBands[${MAX_BANDS}];
  uniform int uBandCount;
  uniform float uBandSharp;
  uniform vec3 uGasSeed;
  uniform float uGasDetail;
  uniform float uTime;
  uniform vec2 uGasRot;     // (cos,sin) rigid zonal rotation (wrapped in JS)
  uniform float uGasSway;   // bounded [-1,1] differential-shear oscillator
  uniform vec4 uGasChurn;   // bounded fBm domain-warp / storm time offsets
  uniform float uTidalLock;
  uniform vec3 uSeaIce;
  uniform float uMoltenLows;
  // Climate (degC, matches the sidebar) + derived obliquity (0..1) + per-kind
  // frost/scorch gates. Per fragment we build a temperature from latitude
  // (rotators) or the substellar angle (tidally locked), then derive frost/scorch.
  uniform float uMeanT;
  uniform float uDayT;
  uniform float uNightT;
  uniform float uObliq;
  uniform float uCanFrost;
  uniform float uCanScorch;
  varying float vElev;
  varying float vLat;

  // Per-fragment surface temperature (degC).
  //  - Rotators: annual-mean insolation falls with latitude (warm equator, cold
  //    poles -> ice caps). Axial tilt (uObliq) flattens the contrast: high tilt gives
  //    the poles a summer, warming them and shrinking the caps. The spread is centred
  //    on the planet mean (0.55 ~ the cos band's area-mean over a sphere) so meanTempC
  //    stays the planet average and the look tracks the sidebar.
  //  - Tidally locked: temperature follows the substellar angle dayDot. Hottest at the
  //    substellar point (pushed hotter than the lit-face average -> the >200C scorch),
  //    temperate near the terminator, coldest on the permanent night face. Uses the
  //    day/night face averages (uDayT/uNightT) so it agrees with the shown numbers.
  float surfaceTempC(float latY, float dayDot) {
    if (uTidalLock > 0.5) {
      float s = clamp(dayDot, -1.0, 1.0);
      float subOver = max(40.0, (uDayT - uMeanT) * 0.7);
      float dayC = mix(uMeanT, uDayT + subOver, smoothstep(0.0, 1.0, s));
      float nightC = mix(uMeanT, uNightT, smoothstep(0.0, -1.0, s));
      return s >= 0.0 ? dayC : nightC;
    }
    float lat = abs(latY);
    float wInsol = mix(cos(lat * 1.5707963), 1.0, 0.5 * uObliq);
    // Equator-to-pole contrast: tied to the model's swing but with a floor large
    // enough that a temperate world's poles get genuinely cold (real ice caps), not
    // merely cool. ~Earth: equator ~mean+25, pole ~mean-30 at low tilt.
    float spread = clamp((uDayT - uNightT) * 0.5 + 50.0, 45.0, 100.0);
    return uMeanT + spread * (wInsol - 0.55);
  }

  vec3 gasBands(float latY) {
    float n = float(uBandCount);
    float lat01 = (latY + 1.0) * 0.5;
    float scaled = lat01 * max(n - 1.0, 1.0);
    int lo = int(min(n - 1.0, floor(scaled)));
    int hi = int(min(n - 1.0, float(lo) + 1.0));
    float frac = scaled - floor(scaled);
    float eased = smoothstep(0.5 - uBandSharp * 0.5, 0.5 + uBandSharp * 0.5, frac);
    return mix(uBands[lo], uBands[hi], eased);
  }

  // Gas-giant cloud deck: the flat latitude bands torn into turbulent, interleaving
  // filaments and eddies (domain-warped fBm), with rare storm vortices that only
  // resolve up close. sdir is the object-space surface direction, so the whole
  // pattern rides rigidly with the planet's spin.
  vec3 gasGiantColor(vec3 sdir) {
    // Differential (zonal) rotation: each latitude drifts in longitude — alternating
    // prograde/retrograde jets — so neighbouring belts shear past one another, the way
    // a real gas giant's bands do. We rotate the SAMPLE point about the polar axis
    // (preserves latitude, only shifts longitude), so the band colour stays pinned to
    // true latitude while all of its turbulent detail flows.
    //
    // The rotation is split into a RIGID whole-deck spin (uGasRot, a wrapped cos/sin
    // pair from JS — it can never shear the structure) plus a small BOUNDED per-latitude
    // countercurrent SWAY (jet * uGasSway): the belts rock past one another within a
    // fixed angular envelope instead of winding apart without bound. Under fast-forward
    // an unbounded uTime*omega differential rotation would wind the noise into sub-pixel
    // striations (→ grain); bounding the sway keeps it structured at any speed, freezing
    // crisply at extreme speed where the churn is imperceptible anyway. (See Clouds.tsx.)
    float jet = sin(sdir.y * 7.0);
    // Per-latitude sway angle, bounded: |GAS_SWAY_AMP * jet * uGasSway| <= GAS_SWAY_AMP.
    // GAS_SWAY_AMP * (sway rate set in JS) matches the old 0.010-rad/s shear slope at 1×.
    const float GAS_SWAY_AMP = 0.6;
    float swayAng = GAS_SWAY_AMP * jet * uGasSway;
    float cs = cos(swayAng), ss = sin(swayAng);
    // Compose the rigid spin (uGasRot) with the bounded sway: a single combined zonal
    // rotation of the sample point about the polar (y) axis. cos/sin add via the angle
    // sum identity, keeping it one rotation (no extra trig of a large argument).
    float ca = uGasRot.x * cs - uGasRot.y * ss;
    float sa = uGasRot.y * cs + uGasRot.x * ss;
    vec3 flowDir = vec3(ca * sdir.x - sa * sdir.z, sdir.y, sa * sdir.x + ca * sdir.z);
    vec3 d = flowDir + uGasSeed; // per-planet offset into the noise field

    // Anisotropic sample frame: compress longitude (x,z), stretch latitude (y) so
    // turbulence shears into east-west streaks (jet-stream flow), not round blobs.
    vec3 fp = vec3(d.x, d.y * 3.0, d.z) * 2.4;

    // Two-level domain warp -> swirling eddies and curled, interleaving band edges.
    // The per-channel churn offsets (uGasChurn.xyz) are BOUNDED oscillators from JS
    // (not a linear uTime ramp) so the fBm sample coordinate never grows large — at
    // extreme fast-forward this freezes the eddies crisply instead of pushing the coord
    // past float32 precision into speckle. At 1× the oscillator slope matches the old
    // ramp, so the eddies churn (born, merge, fade) exactly as before.
    vec3 w = vec3(
      fbm(fp + vec3(uGasChurn.x, 4.7, 0.0)),
      fbm(fp + vec3(0.0, 9.2, uGasChurn.y)),
      fbm(fp + vec3(uGasChurn.z, 1.3, 5.1))
    ) - 0.5;
    float turb = fbm(fp + w * 2.4) - 0.5;

    // Push the band lookup by the turbulence: clean stripes acquire pronounced
    // wavy, braided boundaries that interleave neighbouring bands. A second, larger
    // ripple keeps the waviness visible even when the planet is small on screen.
    // Bands stay anchored to the TRUE latitude (sdir.y), only warped.
    float ripple = (fbm(vec3(d.x * 1.1, d.y * 5.0, d.z * 1.1)) - 0.5);
    // Calm the poles AND the limb. Latitude lines crowd together toward |y|=1 (the
    // poles) and all around the disc edge (the sphere curving away from the eye), so
    // any fine latitude detail there packs many cycles into a pixel and aliases into
    // concentric ring moiré. poleFade handles the true poles; limbFade is an ANALYTIC
    // guard — fwidth() measures how fast the latitude argument changes per screen pixel,
    // and where that exceeds ~one cycle the detail dissolves to clean band rather than
    // shimmering. (fwidth(d.y) == fwidth(sdir.y): the zonal flow rotation preserves y.)
    float poleFade = 1.0 - smoothstep(0.72, 0.97, abs(sdir.y));
    float limbFade = 1.0 - smoothstep(0.5, 1.1, fwidth(d.y * 9.0));
    float latDetail = poleFade * limbFade;
    float warpedLat = clamp(sdir.y + (turb * 0.24 + ripple * 0.10) * latDetail, -1.0, 1.0);
    vec3 col = gasBands(warpedLat);

    if (uGasDetail > 0.001) {
      // Fine zonal streaks within each band. SINGLE octave (snoise) at a MODEST
      // latitude frequency (9, not 14 — fewer lines, so they read as fine banding, not
      // a noisy thicket), and faded by latDetail so they never alias at the poles or
      // the crowded limb where they used to moiré.
      float streak = snoise(vec3(d.x * 1.6, d.y * 9.0, d.z * 1.6) + w);
      col *= 1.0 + streak * 0.13 * uGasDetail * latDetail;

      // Storms: oval vortices. A low-freq field picks the hotspots; the swirl sign
      // splits them into lighter ovals and darker ruddy barges — classic gas-giant
      // features (Jupiter's GRS, its white ovals). Storms ride their latitude's jet
      // (d is already flow-rotated) and slowly form/dissipate via a bounded time offset.
      float spot = fbm(vec3(d.x * 1.6, d.y * 4.0, d.z * 1.6) + 31.0 + uGasChurn.w);
      // Feathered oval mask (wider ramp = soft rim, not a hard-edged blob).
      float storm = smoothstep(0.5, 0.78, spot) * uGasDetail * latDetail;
      // The swirl is what reads as the gyre's spiral. Sample it at a LOW frequency with
      // only a LIGHT domain warp: broad, legible spiral arms instead of the fine, heavily
      // warped thicket that smeared into ugly black-and-white streaks up close. Contrast
      // is softened (paler bright, less-crushed barge) and the light/dark split is a wide
      // gradient, so a gyre reads as a gentle curdle of cloud, not a stark smear.
      float swirl = snoise(fp * 0.7 + w * 0.9);
      vec3 bright = mix(col, vec3(1.0, 0.97, 0.92), 0.5);
      vec3 barge = mix(col, col * 0.55 + vec3(0.1, 0.03, 0.0), 0.8);
      vec3 stormCol = mix(barge, bright, smoothstep(-0.3, 0.3, swirl));
      col = mix(col, stormCol, storm * 0.85);
    }

    return mix(col, uTintColor, uTintPercent);
  }

  // frost/scorch (0..1) are per-fragment, derived from the surface temperature in
  // map_fragment: frost grows the latitudinal ice caps + freezes the sea + (at the
  // extreme) glaciates the disc; scorch parches vegetation to barren brown.
  vec3 biomeColor(float h01, float latY, float frost, float scorch, float bake) {
    if (uIsGas > 0.5) {
      return mix(gasBands(latY), uTintColor, uTintPercent);
    }
    vec3 c;
    if (h01 < uSeaLevel) {
      float oceanT = clamp(h01 / max(uSeaLevel, 1e-6), 0.0, 1.0);
      float t = 1.0 - exp(-oceanT * uOceanFalloff);
      c = mix(uDeepOcean, uShallowOcean, t);
      // Oceans freeze over as the climate cools: liquid sea -> pale sea ice. (No
      // tint on water, matching the original ramp.)
      c = mix(c, uSeaIce, frost);
      // ...and bake off where it is truly searing: a substellar sea boils away to a pale
      // dry evaporite basin instead of staying blue water (the Ocean shell discards its
      // glint over the same cells, so the hot face reads as dry, not sea). Uses the bake
      // term, not scorch, so only a tidally-locked hot face dries; a warm rotator stays wet.
      c = mix(c, vec3(0.66, 0.60, 0.49), bake);
    } else {
      float land01 = clamp((h01 - uSeaLevel) / max(1.0 - uSeaLevel, 1e-6), 0.0, 1.0);
      float lat = abs(latY);
      // Tone the bright beach toward the adjacent lowland so the coast reads as a soft
      // shoreline, not a hard outline ringing every continent.
      vec3 beachC = mix(uBeach, uGrassLow, 0.45);
      vec3 land = mix(beachC, uGrassLow, smoothstep(0.0, uBeachWidth, land01));
      land = mix(land, uGrassHigh, smoothstep(uBeachWidth, uMaxFlat, land01));
      vec3 rock = mix(uRockLow, uRockHigh, land01);
      land = mix(land, rock, smoothstep(uMaxFlat, uMaxFlat + 0.15, land01));
      // Hot climates bake the low vegetation/flats to arid, sun-scorched browns
      // (rock and snow are left alone). Per-fragment scorch: a tidally-locked world's
      // substellar face bakes barren while its terminator ring stays green.
      float veg = 1.0 - smoothstep(uMaxFlat, uMaxFlat + 0.15, land01);
      land = mix(land, vec3(0.40, 0.30, 0.17), scorch * 0.85 * veg);
      // Mountain snow line: the per-kind elevation snow cap on high peaks (kept as-is).
      float snowLine = uSnowEq + (uSnowPole - uSnowEq) * lat;
      land = mix(land, uSnow, smoothstep(snowLine, snowLine + 0.08, land01));
      // Latitudinal / climate ice cap: as the fragment freezes, drag the effective
      // snow line down to sea level so FLAT polar land whitens into a broad cap (not
      // only the peaks). This is what gives cold poles their ice caps.
      float capLine = mix(snowLine, -0.05, frost);
      land = mix(land, uSnow, smoothstep(capLine, capLine + 0.08, land01));
      c = mix(land, uTintColor, uTintPercent);
    }
    // Deep-freeze fragments read as a near-uniform glacial ball, but keep a faint
    // hint of the underlying terrain/ocean so the disc isn't a flat white blank.
    vec3 glacial = mix(vec3(0.84, 0.90, 0.96), c, 0.16);
    return mix(c, glacial, smoothstep(0.5, 1.0, frost));
  }

  // Spiderweb lava veins (emissive worlds): ridged-noise zero-sets form a branching,
  // dendritic network of THIN fissures across the dark crust. A low-freq domain warp
  // makes the channels curve and fork organically rather than reading as a regular
  // cellular grid; a second, finer ridge field adds the small tributary branches. The
  // thresholds sit high (≈0.95) and the base frequency low so only the ridge cores
  // light up — a sparse glowing web over dark basalt, not a dense molten crackle.
  float lavaVeins(vec3 p) {
    vec3 w = vec3(fbm(p * 1.1 + 2.0), fbm(p * 1.1 + 5.2), fbm(p * 1.1 + 9.7)) - 0.5;
    vec3 q = p * 2.3 + w * 1.2;
    float r1 = 1.0 - abs(snoise(q));
    float r2 = 1.0 - abs(snoise(q * 2.1 + 13.0));
    float web = smoothstep(0.95, 1.0, r1) + 0.4 * smoothstep(0.965, 1.0, r2);
    return clamp(web, 0.0, 1.0);
  }
`;

// Coastal-city uniforms (filled per focused planet by Planet.tsx from cities.ts) plus
// the cellular-noise helpers the night lights use to place INDIVIDUAL point-lights.
const CITY_PARS = /* glsl */ `
  uniform int uCityCount;
  uniform vec4 uCity[${MAX_CITIES}]; // xyz: object-space dir, w: angular radius
  uniform float uCityUrban;          // 0..1 global urbanization (ecumenopolis fill)
  uniform vec3 uCityLight;
  uniform vec3 uCityDayColor;
  uniform float uCityGlow;

  // Cheap 3D hash → a feature point + intensity per cell.
  vec3 cityHash33(vec3 p) {
    p = fract(p * vec3(0.1031, 0.1030, 0.0973));
    p += dot(p, p.yxz + 33.33);
    return fract((p.xxy + p.yxx) * p.zyx);
  }
  // Worley/cellular F1: returns vec2(distance to nearest feature point, that cell's
  // random intensity). One feature point per cell → one city light per cell, so the
  // night side reads as a field of DISCRETE lights, not a continuous glow.
  vec2 cityCells(vec3 p) {
    vec3 ip = floor(p);
    vec3 fp = fract(p);
    float f1 = 1e9;
    float inten = 0.0;
    for (int x = -1; x <= 1; x++)
    for (int y = -1; y <= 1; y++)
    for (int z = -1; z <= 1; z++) {
      vec3 g = vec3(float(x), float(y), float(z));
      vec3 h = cityHash33(ip + g);
      vec3 d = g + h - fp;
      float dist2 = dot(d, d);
      if (dist2 < f1) { f1 = dist2; inten = fract(h.x * 6.7 + h.y * 3.1); }
    }
    return vec2(sqrt(f1), inten);
  }
`;

// One onBeforeCompile: albedo from the biome ramp, shiny water (sun glint), coastal
// cities (day sprawl + night lights), and the analytic eclipse shadow on the direct
// sun term only.
export function planetSurfacePatch(shadow: ShadowUniforms, biome: BiomeUniforms, city: CityUniforms) {
  return (shader: WebGLProgramParametersWithUniforms) => {
    Object.assign(shader.uniforms, shadow, biome, city);
    shader.vertexShader = shader.vertexShader
      .replace(
        '#include <common>',
        `#include <common>
         attribute float aElevation;
         attribute float aLatitude;
         varying float vElev;
         varying float vLat;
         varying vec3 vObjDir;
         varying vec3 vShadowWorld;
         varying vec3 vWorldNormal;`,
      )
      .replace(
        '#include <begin_vertex>',
        `#include <begin_vertex>
         vElev = aElevation;
         vLat = aLatitude;
         vObjDir = normalize(position);`,
      )
      .replace(
        '#include <worldpos_vertex>',
        `#include <worldpos_vertex>
         vShadowWorld = (modelMatrix * vec4(transformed, 1.0)).xyz;
         // Object-space sphere normal -> world (uniform planet scale), for the
         // tidal-lock day/night frost term in the fragment shader.
         vWorldNormal = normalize(mat3(modelMatrix) * normalize(position));`,
      );
    shader.fragmentShader = shader.fragmentShader
      .replace(
        '#include <common>',
        `#include <common>
         varying vec3 vObjDir;
         varying vec3 vShadowWorld;
         varying vec3 vWorldNormal;
         ${SHADOW_PARS}
         ${SIMPLEX3D}
         ${FBM3D}
         ${BIOME_PARS}
         ${CITY_PARS}
         // Warped elevation + per-fragment frost (set in map_fragment), shared with
         // roughnessmap_fragment; gLava is the spiderweb fissure intensity, shared
         // with emissivemap_fragment so the veins glow on the night side too. gCity is
         // the per-fragment urban DENSITY (0..1: discrete metro footprints, rising to
         // a planet-wide ecumenopolis as uCityUrban→1) and gDayDot the day/night term
         // — both set in map_fragment and reused by the city lights in
         // emissivemap_fragment.
         float gElev;
         float gFrost;
         float gScorch;
         float gBake;
         float gLava;
         float gCity;
         float gDayDot;`,
      )
      .replace(
        '#include <map_fragment>',
        `#include <map_fragment>
         {
           vec3 sdir = normalize(vObjDir);
           vec3 albedo;
           gLava = 0.0;
           gScorch = 0.0;
           gBake = 0.0;
           gCity = 0.0;
           gDayDot = 1.0;
           if (uIsGas > 0.5) {
             gElev = vElev;
             gFrost = 0.0;
             albedo = gasGiantColor(sdir);
           } else {
             // Warp the interpolated height with low-freq noise so coastlines and snow
             // lines get a ragged, natural edge instead of perfect contour rings.
             float warp = fbm(sdir * 4.0 + 11.0) - 0.5;
             gElev = clamp(vElev + warp * 0.08, 0.0, 1.0);
             // Per-fragment climate: build a surface temperature from latitude
             // (rotators: cold poles -> caps, flattened by axial tilt) or the
             // substellar angle (tidally locked: scorched substellar, temperate
             // terminator, frozen night face), then derive frost + scorch from it,
             // each gated by the per-kind canFrost/canScorch flag. The frost ramp
             // starts well above freezing (≈8°C) so a temperate world grows broad
             // polar caps, not just summit snow.
             float dayDot = dot(normalize(vWorldNormal), normalize(uSunPosW - vShadowWorld));
             float tempC = surfaceTempC(vLat, dayDot);
             gFrost = uCanFrost * smoothstep(8.0, -35.0, tempC);
             gScorch = uCanScorch * smoothstep(40.0, 95.0, tempC);
             // Boil-off: only a searing (substellar / molten-hot) surface, well above the
             // vegetation-scorch band, so a warm rotator's seas stay liquid; this is what
             // dries the seabed + kills the ocean shell's glint on a tidally-locked hot face.
             gBake = uCanScorch * smoothstep(80.0, 170.0, tempC);
             albedo = biomeColor(gElev, vLat, gFrost, gScorch, gBake);
             // Gentle large-scale mottle so flat biome interiors read as terrain, not a
             // perfectly even paint — subtle, low-freq, so it never speckles (water
             // keeps its smooth sheen).
             if (gElev >= uSeaLevel) {
               float mottle = fbm(sdir * 10.0);
               albedo *= 0.9 + 0.16 * mottle;
             }
             // Emissive worlds (molten / volcanic / broken): a glowing spiderweb of
             // lava fissures threads the dark crust, concentrated toward the lows where
             // lava pools and cools. Painted into the albedo here so it reads on the
             // sunlit side; emissivemap_fragment also self-illuminates it for the night.
             if (dot(emissive, vec3(1.0)) > 0.001) {
               float pool = 1.0 - smoothstep(uSeaLevel, uSeaLevel + 0.45, gElev);
               gLava = lavaVeins(sdir) * (0.5 + 0.5 * pool);
               vec3 veinCol = mix(vec3(0.95, 0.28, 0.06), vec3(1.7, 1.05, 0.4), gLava);
               albedo = mix(albedo, veinCol, clamp(gLava, 0.0, 1.0));
             }
             // Coastal cities: gCity is the per-fragment habitation DENSITY (0..1) on
             // land. Rather than discrete city discs, settlement is a web that follows
             // geography: each city sprawls out with a soft falloff and neighbours merge
             // into a connected network, but it only takes hold on LOW, COASTAL land in
             // TEMPERATE latitudes — so the lit pattern hugs valleys and coastlines and
             // leaves highlands, ice and ocean dark, the way real city lights do. A
             // global urbanization floor (uCityUrban) floods it into a planet-wide
             // ecumenopolis that overrides geography. Reused by the night lights below;
             // hold gDayDot for that pass.
             gDayDot = dayDot;
             if (gElev >= uSeaLevel && (uCityCount > 0 || uCityUrban > 0.0)) {
               float aboveSea = gElev - uSeaLevel;
               // Suitability: low coastal land that is LIVABLE — neither frosted (ice
               // cap) nor scorched (desert), the same gates the biome uses — so the lit
               // web hugs temperate coasts/valleys and leaves poles, peaks and baking
               // equators dark. The ecumenopolis overrides it (paves it all).
               float lowland = 1.0 - smoothstep(0.03, 0.24, aboveSea);
               float habT = clamp(1.0 - gFrost - gScorch, 0.0, 1.0);
               float geo = mix(lowland * habT, 1.0, uCityUrban);
               // Population reach: each city's settlement sprawls with a soft Gaussian
               // falloff (radius ≫ its core), so neighbouring cities' sprawl overlaps
               // into one connected network instead of isolated dots.
               float reach = uCityUrban;
               for (int i = 0; i < ${MAX_CITIES}; i++) {
                 if (i >= uCityCount) break;
                 float sprawl = clamp(uCity[i].w * 3.0, 0.12, 0.30);
                 float d = distance(sdir, uCity[i].xyz);
                 reach = max(reach, exp(-(d * d) / (sprawl * sprawl)));
               }
               gCity = clamp(reach * geo, 0.0, 1.0);
               if (gCity > 0.003) {
                 // Day: concrete reads only at the dense cores (gCity²), faintly textured;
                 // the thinner sprawl keeps its natural ground colour.
                 float tex = 0.78 + 0.22 * fbm(sdir * 130.0);
                 albedo = mix(albedo, uCityDayColor * tex, clamp(gCity * gCity * 0.7, 0.0, 1.0));
               }
             }
           }
           diffuseColor.rgb = albedo;
         }`,
      )
      .replace(
        '#include <roughnessmap_fragment>',
        `#include <roughnessmap_fragment>
         // Specular highlights belong only to liquid: water is tight and glossy (a
         // small sharp sun glint), land grades to fully matte (no broad plastic
         // sheen), and gas giants — no liquid surface at all — are fully matte.
         if (uIsGas > 0.5) {
           roughnessFactor = 1.0;
         } else {
           // Liquid water is glossy (tight glint); as the sea freezes OR bakes dry it
           // turns matte (ice / evaporite) and the glint fades; land is fully matte. So a
           // tidally-locked world's frozen night face and scorched substellar basin both
           // lose their glint, leaving the shine only on the temperate liquid ring.
           float water = mix(0.09, 0.86, clamp(gFrost + gBake, 0.0, 1.0));
           roughnessFactor = mix(water, 1.0, smoothstep(uSeaLevel - 0.015, uSeaLevel + 0.015, gElev));
         }`,
      )
      .replace(
        '#include <emissivemap_fragment>',
        `#include <emissivemap_fragment>
         // Emissive worlds (molten/volcanic/broken) must glow ONLY from the lava in
         // their lows + fissures, never the whole crust — otherwise the planet reads
         // as a flat glowing ball. Ramp the self-illumination up below the lava line
         // (with a hotter bloom in the deepest channels) and leave the high rock dark.
         if (dot(emissive, vec3(1.0)) > 0.001) {
           float lava = 1.0 - smoothstep(uSeaLevel - 0.03, uSeaLevel + 0.06, gElev);
           float core = 1.0 - smoothstep(0.0, max(uSeaLevel, 1e-4), gElev);
           // Broad molten-lows glow only for genuinely molten worlds (uMoltenLows);
           // a volcanic world (uMoltenLows≈0) keeps a DARK crust and glows ONLY along
           // its spiderweb fissures (×2.0) — bright on both the day and night sides —
           // while the high cooled rock between the veins stays dark.
           float lows = lava * (1.0 + 1.8 * core) * uMoltenLows;
           float glow = max(lows, gLava * 2.0);
           totalEmissiveRadiance *= glow;
         }
         // Coastal city lights: a field of INDIVIDUAL point-lights on the NIGHT side only
         // (daylight washes them out). gCity (set in map_fragment) is the geography-carved
         // habitation density, so lights only appear along valleys/coasts and fade into
         // dark countryside. Each cellular feature point is one light, drawn as a soft dot
         // whose edge is kept ~1px wide via fwidth — so up close they read as separate
         // lights, and far away (cells sub-pixel) they blend, via an LOD term, into a soft
         // atmosphere-scattered glow rather than aliasing into hard pixels. Additive and
         // independent of the lava emissive above, so habitable worlds light up too.
         if (gCity > 0.003) {
           float night = smoothstep(0.12, -0.22, gDayDot);
           if (night > 0.001) {
             vec3 ndir = normalize(vObjDir);
             // Town clumping so lights gather into settlements with dark gaps between.
             float clump = smoothstep(0.34, 0.8, fbm(ndir * 16.0));
             float dens = clamp(gCity * (0.25 + 1.05 * clump), 0.0, 1.0);
             // Individual lights from a cellular field: one small soft dot per cell. A high
             // cell frequency packs in many tiny lights.
             vec2 cell = cityCells(ndir * 190.0);
             float aa = max(fwidth(cell.x), 1e-4);
             float dotR = 0.34;
             float dot1 = smoothstep(dotR + aa, max(dotR - aa, 0.0), cell.x);
             // Most cells are lit where habitation is dense, thinning to a few bright ones
             // in the hinterland; each light's brightness varies (some bright, some dim).
             float litCell = smoothstep(1.0 - dens, 1.0 - dens + 0.18, cell.y);
             float dots = dot1 * litCell * (0.35 + 0.65 * cell.y);
             // LOD: keep the discrete dots until the cells fall well below a pixel (aa
             // large = very distant), only then blending into a smooth glow so far worlds
             // don't speckle. Tuned so the dots stay crisp at the focused viewing range.
             float lod = smoothstep(1.4, 0.5, aa);
             float smoothGlow = dens * (0.5 + 0.5 * clump);
             // Atmospheric scatter: a soft, broad warm haze under the sharp dots, following
             // the smooth habitation density (gCity) — so the lights sit in a gentle glow,
             // as light scattered through the air, rather than as bare points on black.
             float scatter = gCity * 0.28;
             float glow = mix(smoothGlow, dots + scatter, lod);
             // Warm sodium glow; the brightest, densest cores burn toward white.
             vec3 col = mix(uCityLight, vec3(1.0, 0.96, 0.9), smoothstep(0.45, 1.0, dens));
             totalEmissiveRadiance += col * (glow * night * uCityGlow);
           }
         }`,
      )
      .replace(
        '#include <lights_fragment_begin>',
        `#include <lights_fragment_begin>
         {
           float _vis = sunVisibility(vShadowWorld);
           reflectedLight.directDiffuse *= _vis;
           reflectedLight.directSpecular *= _vis;
         }`,
      );
  };
}
